import { useCallback, useSyncExternalStore } from "react";

const STORAGE_KEY = "hga-progress";

// Module-level store shared across all hook callers
let state = (() => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
})();
const listeners = new Set();

function emit(next) {
  state = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // storage unavailable — ignore
  }
  listeners.forEach((l) => l());
}

function subscribe(l) {
  listeners.add(l);
  return () => listeners.delete(l);
}

// Sync across tabs / other storage writers
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key !== STORAGE_KEY) return;
    try {
      state = e.newValue ? JSON.parse(e.newValue) : {};
    } catch {
      state = {};
    }
    listeners.forEach((l) => l());
  });
}

const getSnapshot = () => state;

export function useProgress() {
  const completed = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const isDone = useCallback((id) => Boolean(completed[id]), [completed]);

  const toggle = useCallback((id) => {
    const next = { ...state };
    if (next[id]) delete next[id];
    else next[id] = Date.now();
    emit(next);
  }, []);

  const markDone = useCallback((id) => {
    if (state[id]) return;
    emit({ ...state, [id]: Date.now() });
  }, []);

  const reset = useCallback(() => emit({}), []);

  return { completed, isDone, toggle, markDone, reset };
}
